import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { GeneralErrorMessage } from '@core/constants/messages';
import { AuthorizationPacimService } from 'src/app/core';
import { EligibleDates, TherapyMinutes, TherapyMinutesIteration } from '@core/model';
import { AuthorizationReferenceProductDTO } from '@shared/model';
import { DateUtilities, TableColumn } from '@mynexus/mynexus-ui-lib';
import { dateWithoutMilliseconds } from '@shared/utility';
import { ConfirmationService, ToastMessageOptions, MessageService } from 'primeng/api';
import { finalize } from 'rxjs';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'therapy-minutes-expanded',
    templateUrl: './therapy-minutes-expanded.component.html',
    styleUrls: ['./therapy-minutes-expanded.component.scss'],
    standalone: false
})
export class TherapyMinutesExpandedComponent implements OnInit {
  @Input() eligibleDatesForTherapy: EligibleDates[];
  @Input() canEdit: boolean;
  @Input() previousPeriod: TherapyMinutesIteration;
  @Input() currentPeriod: TherapyMinutesIteration;
  @Input() disciplines: AuthorizationReferenceProductDTO[];
  @Input() tenantId: string;
  @Input() authorizationByProductId: string;
  @Input() isInternal = false;

  @Output() updateMinutes = new EventEmitter<TherapyMinutes[]>();

  therapyForm: FormGroup;
  cols = [
    new TableColumn('Discipline', 'disciplineName', '12rem'),
  ];

  eligibleDates: string[];
  missedDaysMessage: ToastMessageOptions;
  suggestLevelMessage: ToastMessageOptions;
  isLoading = false;
  totalMinutes: Record<string, number> = {};

  get therapies(): FormArray<FormGroup> {
    return this.therapyForm.get('therapies') as FormArray<FormGroup>;
  }

  constructor(
    private formBuilder: FormBuilder,
    private authorizationPacimService: AuthorizationPacimService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
  ){}

  ngOnInit(): void {
    this.therapyForm = new FormGroup({
      therapies: this.formBuilder.array([]),
    });

    this.missedDaysMessage = {
      summary: 'Entries Not Allowed',
      detail: 'No dates in this time frame are eligible for level adjustment request.',
    };

    this.getEligibleDates();
    this.initTable();
    this.createForm();
  }

  discardChanges(): void {
    this.confirmationService.confirm({
      key: 'unsaved-changes-auth-detail',
      message:
        'You have unsaved changes that will be lost. Are you sure you want to continue?',
      accept: () => {
        this.cancelChanges();
        this.getTotalMinutes();
      },
    });
  }

  cancelChanges(): void {
    this.setFormValue();

    this.therapyForm.markAsPristine();
    this.therapyForm.markAsUntouched();
  }

  saveChanges(): void {
    this.isLoading = true;

    const modifiedRecords = this.therapies.controls
      .map(formGroup => Object.values(formGroup.controls).filter(control => control.dirty))
      .flat()
      .map(control => control.value);

    const payload = {
      upsert: modifiedRecords
        .filter(record => !!record.timeSpent)
        .map(record => {
          return {
            ...record,
            authorizationTherapyId: record.authorizationTherapyId ? record.authorizationTherapyId : undefined,
          };
        }),
      delete: modifiedRecords
        .filter(record => !record.timeSpent)
        .map(record => record.authorizationTherapyId),
    };

    this.authorizationPacimService.updateTherapyMinutes(this.tenantId, this.authorizationByProductId, payload).pipe(
      finalize(() => this.isLoading = false),
    ).subscribe({
        next: res => {
          this.updateMinutes.emit(res);

          this.messageService.add({
            key: 'therapy-minutes',
            severity: 'success',
            summary: 'Therapy Minutes Saved',
            detail: 'Therapy minutes were successfully saved.',
            life: 5000,
          });
        },
        error: () => {
          this.messageService.add({
            key: 'therapy-minutes',
            severity: 'error',
            summary: 'Error',
            detail: GeneralErrorMessage,
            life: 5000,
          });
        },
      });
  }

  getTotalMinutes(): void {
    this.eligibleDates.forEach(date => {
      this.totalMinutes[date] = this.therapies.value
        .reduce((accumulator, currentValue) => accumulator + currentValue[date].timeSpent, 0);
    });

    if (this.currentPeriod.dates?.length < 7 && this.currentPeriod.weekNumber !== 1) {
      const additionalDatesFromPreviousWeek = this.previousPeriod.dates.slice(this.currentPeriod.dates.length);
      const addedMinutesFromPreviousWeek = this.previousPeriod.addedMinutes
        ?.filter(date => additionalDatesFromPreviousWeek.includes(date.therapyDate));

      additionalDatesFromPreviousWeek.forEach(date => {
        this.totalMinutes[date] = addedMinutesFromPreviousWeek
          .filter(item => item.therapyDate === date)
          .reduce((accumulator, currentValue) => accumulator + currentValue.timeSpent, 0);
      });
    }

    this.calculateLevel();
  }


  onInputMinutes(cellGroup: FormGroup): void {
    if (cellGroup.value.authorizationTherapyId || cellGroup.value.timeSpent > 0) {
      return;
    }

    cellGroup.get('timeSpent').markAsPristine();
  }

  private calculateLevel(): void {
    const daysWithEnoughMinutes = Object.values(this.totalMinutes).filter(minutes => minutes >= 90)?.length;

    if ((this.currentPeriod.weekNumber === 1 && this.currentPeriod.dates?.length < 7) || daysWithEnoughMinutes < 3) {
      this.suggestLevelMessage = null;
    } else {
      const levelType = daysWithEnoughMinutes >= 5
        ? 3
        : 2;

      this.suggestLevelMessage = {
        summary: 'Reimbursement Level Reached',
        detail: this.isInternal
          ? `Reimbursement level criteria met for level ${levelType}.`
          : `You entered enough therapy minutes to request a reimbursement of level ${levelType}.`,
      };
    }
  }

  private getEligibleDates(): void {
    this.eligibleDates = this.eligibleDatesForTherapy
    .map(range => {
      return DateUtilities.getDatesRange(new Date(range.startDate.replace('Z', '')), new Date(range.endDate.replace('Z', '')))
        .map(item => dateWithoutMilliseconds(DateUtilities.convertToUTCStartOfDayDate(item)?.toISOString()));
    })
    .flat()
    .filter(item => this.currentPeriod.dates.includes(item))
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  }

  private initTable(): void {
    this.eligibleDates.forEach(date => {
      this.cols.push(new TableColumn(`${date}`, `${date}`, '19rem'));
    })
  }

  private createForm(): void {
    this.disciplines.forEach(discipline => {
      const controls = this.createControls(discipline);

      this.therapies.push(controls, { emitEvent: false });
    });

    this.setFormValue();
    this.getTotalMinutes();
  }

  private createControls(discipline: AuthorizationReferenceProductDTO) {
    const group = new FormGroup({});
    group.addControl('disciplineName', new FormControl(discipline.description));

    this.eligibleDates.forEach(date => {
      group.addControl(date, new FormGroup({
        timeSpent: new FormControl(null, [Validators.min(0), Validators.max(1440)]),
        authorizationTherapyId: new FormControl(),
        therapyDiscipline: new FormControl(discipline),
        therapyDate: new FormControl(date),
        timeUnit: new FormControl('Minute'),
      }));
    });

    return group;
  }

  private setFormValue(): void {
    this.therapies.controls.forEach(controlsGroup => {
      Object.entries(controlsGroup.controls)
        .filter(([label]) => label !== 'disciplineName')
        .forEach(([label, control]) => {
          const filledMinutes: TherapyMinutes = this.currentPeriod.addedMinutes?.find(item => {
            return item.therapyDate === label
              && item.therapyDiscipline.authorizationReferenceByProductId === control.value.therapyDiscipline.authorizationReferenceByProductId;
          });

          control.patchValue(filledMinutes ? { ...filledMinutes } : { timeSpent: null });
        });
    });
  }
}
